/** @module Actions for many selected copies: condition, language, tags, price paid, sell, archive, delete. */
import { useMutation } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  Ellipsis,
  GalleryVerticalEnd,
  HandCoins,
  Languages,
  Package,
  ReceiptEuro,
  Sparkles,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { newPurchase, PurchaseFields, purchaseBody } from "@/components/purchase-fields";
import { ComboInput } from "@/components/ui/combo-input";
import { Segmented } from "@/components/ui/controls";
import { Sheet } from "@/components/ui/overlays";
import { treeOrder, useLocations } from "@/lib/locations";
import { numberedNames } from "@/lib/suggestions";
import { ARCHIVE_REASONS, CONDITIONS, LANGUAGES } from "@/lib/collection";
import { SalesSheet } from "@/features/collection/sales-sheet";
import { useCreateTag, useTags } from "@/features/collection/tags";

type Action =
  | "set_condition"
  | "set_language"
  | "archive"
  | "restore"
  | "add_tags"
  | "remove_tags"
  | "delete"
  | "set_purchase"
  | "clear_purchase"
  | "set_location";

type Menu =
  | "condition"
  | "language"
  | "tags"
  | "location"
  | "purchase"
  | "archive"
  | "delete"
  | "more"
  | null;
type Split = "each" | "total";

function BarButton({
  icon,
  label,
  onClick,
  disabled,
  wideOnly = false,
  phoneOnly = false,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled: boolean;
  /** Phones keep these under "Mehr"; five buttons is what fits next to each other. */
  wideOnly?: boolean;
  phoneOnly?: boolean;
}) {
  const display = wideOnly ? "hidden md:flex" : phoneOnly ? "flex md:hidden" : "flex";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`${display} min-h-11 flex-1 flex-col items-center justify-center gap-[2px] rounded-lg text-[11px] font-semibold disabled:opacity-50 md:flex-none md:flex-row md:gap-[6px] md:px-3 md:text-[13px] [&_svg]:size-[18px] md:[&_svg]:size-4`}
    >
      {icon}
      {label}
    </button>
  );
}

export function BulkBar({
  ids,
  archived,
  onDone,
  onClear,
}: {
  ids: string[];
  /** The list shows archived copies: the action there is bringing them back. */
  archived: boolean;
  onDone: () => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [menu, setMenu] = useState<Menu>(null);
  const [newTag, setNewTag] = useState("");
  const [newPlace, setNewPlace] = useState("");
  const locations = useLocations(menu === "location");
  const [purchase, setPurchase] = useState(newPurchase);
  const [split, setSplit] = useState<Split>("each");
  const [selling, setSelling] = useState(false);
  const priced = purchaseBody(purchase);
  const tags = useTags();
  const createTag = useCreateTag();

  const run = useMutation({
    mutationFn: async ({ action, value }: { action: Action; value?: unknown }) => {
      const { data, response } = await api.POST("/api/v1/collection/bulk", {
        body: { action, item_ids: ids, value },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: () => {
      setMenu(null);
      setPurchase(newPurchase());
      onDone();
    },
  });

  const busy = run.isPending || createTag.isPending;
  const close = () => {
    setMenu(null);
  };
  const titles: Record<Exclude<Menu, null>, string> = {
    condition: t("collection.bulk.setCondition"),
    language: t("collection.bulk.setLanguage"),
    tags: t("collection.tags"),
    location: t("locations.move"),
    purchase: t("purchase.bulk.title"),
    archive: t("collection.bulk.why"),
    delete: t("collection.bulk.delete"),
    more: t("transfer.more"),
  };

  return (
    <>
      {/* Phones: the bar takes the place of the tab bar. Wide screens: it floats. */}
      <div className="fixed inset-x-0 bottom-0 z-30 bg-(--bg) px-3 pt-2 pb-[max(12px,env(safe-area-inset-bottom))] md:inset-x-auto md:bottom-6 md:left-[calc(50%+var(--sidebar-w)/2)] md:-translate-x-1/2 md:bg-transparent md:p-0">
        <div
          role="toolbar"
          aria-label={t("collection.bulk.label")}
          className="flex items-center gap-[6px] rounded-[var(--radius-md)] bg-(--text) px-3 py-[10px] text-(--bg) shadow-[var(--shadow-3)] md:py-2 md:pr-2 md:pl-4"
        >
          <b className="t-sm hidden whitespace-nowrap md:inline">
            {t("collection.bulk.selected", { count: ids.length })}
          </b>
          <span className="hidden h-6 w-px bg-current opacity-30 md:block" />
          <BarButton
            icon={<Sparkles />}
            label={t("collection.condition")}
            disabled={busy}
            onClick={() => {
              setMenu("condition");
            }}
          />
          <BarButton
            wideOnly
            icon={<Languages />}
            label={t("collection.language")}
            disabled={busy}
            onClick={() => {
              setMenu("language");
            }}
          />
          <BarButton
            wideOnly
            icon={<Tag />}
            label={t("collection.tags")}
            disabled={busy}
            onClick={() => {
              setMenu("tags");
            }}
          />
          <BarButton
            wideOnly
            icon={<Package />}
            label={t("locations.label")}
            disabled={busy}
            onClick={() => {
              setMenu("location");
            }}
          />
          <BarButton
            icon={<ReceiptEuro />}
            label={t("purchase.bulk.button")}
            disabled={busy}
            onClick={() => {
              setMenu("purchase");
            }}
          />
          {archived ? null : (
            <BarButton
              icon={<HandCoins />}
              label={t("transfer.sales.button")}
              disabled={busy}
              onClick={() => {
                setSelling(true);
              }}
            />
          )}
          {archived ? (
            <BarButton
              icon={<ArchiveRestore />}
              label={t("collection.restore")}
              disabled={busy}
              onClick={() => {
                run.mutate({ action: "restore" });
              }}
            />
          ) : (
            <BarButton
              icon={<Archive />}
              label={t("collection.archive")}
              disabled={busy}
              onClick={() => {
                setMenu("archive");
              }}
            />
          )}
          <BarButton
            wideOnly
            icon={<Trash2 />}
            label={t("collection.bulk.delete")}
            disabled={busy}
            onClick={() => {
              setMenu("delete");
            }}
          />
          <BarButton
            phoneOnly
            icon={<Ellipsis />}
            label={t("transfer.more")}
            disabled={busy}
            onClick={() => {
              setMenu("more");
            }}
          />
          <span className="hidden h-6 w-px bg-current opacity-30 md:block" />
          <button
            type="button"
            aria-label={t("collection.bulk.clear")}
            onClick={onClear}
            className="hidden min-h-10 place-items-center rounded-lg px-3 md:grid"
          >
            <X className="size-4" />
          </button>
        </div>
        {run.isError || createTag.isError ? (
          <p role="alert" className="t-sm loss pt-2 text-center">
            {t("review.error.generic")}
          </p>
        ) : null}
      </div>

      <Sheet open={menu !== null} onClose={close} label={menu ? titles[menu] : ""}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="t-h3">{menu ? titles[menu] : ""}</h2>
            <span className="t-sm t-muted">
              {t("collection.bulk.selected", { count: ids.length })}
            </span>
          </div>

          {menu === "more" ? (
            <div className="flex flex-col">
              {(
                [
                  ["language", <Languages key="l" />, t("collection.language")],
                  ["tags", <Tag key="t" />, t("collection.tags")],
                  ["location", <Package key="p" />, t("locations.label")],
                  ["delete", <Trash2 key="d" />, t("collection.bulk.delete")],
                ] as const
              ).map(([target, icon, label]) => (
                <button
                  key={target}
                  type="button"
                  className="row min-h-12 gap-3 border-b border-(--border) last:border-0 [&_svg]:size-5"
                  onClick={() => {
                    setMenu(target);
                  }}
                >
                  {icon}
                  {label}
                </button>
              ))}
            </div>
          ) : null}

          {menu === "condition" ? (
            <div className="flex flex-wrap gap-2">
              {CONDITIONS.map((condition) => (
                <button
                  key={condition}
                  type="button"
                  disabled={busy}
                  className="chip num"
                  onClick={() => {
                    run.mutate({ action: "set_condition", value: condition });
                  }}
                >
                  {condition}
                </button>
              ))}
            </div>
          ) : null}

          {menu === "language" ? (
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((language) => (
                <button
                  key={language}
                  type="button"
                  disabled={busy}
                  className="chip"
                  onClick={() => {
                    run.mutate({ action: "set_language", value: language });
                  }}
                >
                  {language.toUpperCase()}
                </button>
              ))}
            </div>
          ) : null}

          {menu === "purchase" ? (
            <div className="flex flex-col gap-4">
              <Segmented
                label={t("purchase.bulk.mode")}
                value={split}
                onChange={setSplit}
                options={[
                  { value: "each", label: t("purchase.bulk.each") },
                  { value: "total", label: t("purchase.bulk.total") },
                ]}
              />
              <PurchaseFields draft={purchase} onChange={setPurchase} />
              <p className="t-sm t-muted">
                {split === "each" ? t("purchase.bulk.eachHint") : t("purchase.bulk.totalHint")}
              </p>
              <div className="row justify-end gap-2">
                <button
                  type="button"
                  disabled={busy}
                  className="btn btn-ghost"
                  onClick={() => {
                    run.mutate({ action: "clear_purchase" });
                  }}
                >
                  {t("purchase.bulk.clear")}
                </button>
                <button
                  type="button"
                  disabled={busy || !priced?.purchase_price}
                  className="btn btn-primary"
                  onClick={() => {
                    if (!priced?.purchase_price) return;
                    run.mutate({
                      action: "set_purchase",
                      value: {
                        price: priced.purchase_price,
                        mode: split,
                        purchased_on: priced.purchased_on,
                        source: priced.purchase_source,
                      },
                    });
                  }}
                >
                  {t("purchase.bulk.apply")}
                </button>
              </div>
            </div>
          ) : null}

          {menu === "archive" ? (
            <div className="flex flex-wrap gap-2">
              {ARCHIVE_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  disabled={busy}
                  className="chip"
                  onClick={() => {
                    run.mutate({ action: "archive", value: reason });
                  }}
                >
                  {t(`collection.reason.${reason}`)}
                </button>
              ))}
            </div>
          ) : null}

          {menu === "location" ? (
            <div className="flex flex-col gap-3">
              <ul className="flex flex-col">
                {treeOrder(locations.data?.items ?? []).map(({ location, depth }) => (
                  <li key={location.id}>
                    <button
                      type="button"
                      disabled={busy}
                      className="row min-h-12 w-full gap-3 border-b border-(--border) text-left"
                      style={{ paddingLeft: `${String(depth * 20)}px` }}
                      onClick={() => {
                        run.mutate({ action: "set_location", value: location.id });
                      }}
                    >
                      {location.kind === "deck" ? (
                        <GalleryVerticalEnd className="size-4 shrink-0 text-(--text-muted)" />
                      ) : (
                        <Package className="size-4 shrink-0 text-(--text-muted)" />
                      )}
                      <span className="truncate">{location.name}</span>
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    disabled={busy}
                    className="row min-h-12 w-full gap-3 text-left text-(--text-muted)"
                    onClick={() => {
                      run.mutate({ action: "set_location", value: null });
                    }}
                  >
                    {t("locations.none")}
                  </button>
                </li>
              </ul>
              <form
                className="row items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!newPlace.trim()) return;
                  void api
                    .POST("/api/v1/locations", { body: { name: newPlace.trim(), kind: "box" } })
                    .then(({ data }) => {
                      if (!data) return;
                      setNewPlace("");
                      run.mutate({ action: "set_location", value: data.id });
                    });
                }}
              >
                <span className="grow">
                  <ComboInput
                    label={t("locations.newBox")}
                    value={newPlace}
                    maxLength={80}
                    onChange={setNewPlace}
                    groups={[
                      {
                        label: t("suggest.presets"),
                        options: numberedNames(
                          [t("locations.kind.box")],
                          (locations.data?.items ?? []).map((location) => location.name),
                        ),
                      },
                    ]}
                  />
                </span>
                <button
                  type="submit"
                  disabled={busy || !newPlace.trim()}
                  className="btn btn-secondary"
                >
                  {t("locations.createAndMove")}
                </button>
              </form>
            </div>
          ) : null}

          {menu === "tags" ? (
            <div className="flex flex-col gap-3">
              {(tags.data ?? []).length === 0 ? (
                <span className="t-sm t-muted">{t("collection.bulk.noTags")}</span>
              ) : (
                <ul className="flex flex-col">
                  {(tags.data ?? []).map((tag) => (
                    <li
                      key={tag.id}
                      className="row-between min-h-12 border-b border-(--border) last:border-0"
                    >
                      <span className="badge badge-neutral">{tag.name}</span>
                      <span className="row gap-2">
                        <button
                          type="button"
                          disabled={busy}
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            run.mutate({ action: "add_tags", value: [tag.id] });
                          }}
                        >
                          {t("collection.bulk.addTag")}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            run.mutate({ action: "remove_tags", value: [tag.id] });
                          }}
                        >
                          {t("collection.bulk.removeTag")}
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <form
                className="row gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!newTag.trim()) return;
                  createTag.mutate(newTag, {
                    onSuccess: (tag) => {
                      setNewTag("");
                      run.mutate({ action: "add_tags", value: [tag.id] });
                    },
                  });
                }}
              >
                <input
                  value={newTag}
                  maxLength={40}
                  onChange={(event) => {
                    setNewTag(event.target.value);
                  }}
                  placeholder={t("collection.bulk.newTag")}
                  aria-label={t("collection.bulk.newTag")}
                  className="input grow"
                />
                <button
                  type="submit"
                  disabled={busy || !newTag.trim()}
                  className="btn btn-secondary"
                >
                  {t("collection.bulk.createAndAdd")}
                </button>
              </form>
            </div>
          ) : null}

          {menu === "delete" ? (
            <div className="flex flex-col gap-4">
              <p className="callout callout-warn">
                {t("collection.bulk.deleteConfirm", { count: ids.length })}
              </p>
              <div className="row justify-end gap-2">
                <button type="button" className="btn btn-ghost" onClick={close}>
                  {t("collection.bulk.cancel")}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className="btn btn-danger"
                  onClick={() => {
                    run.mutate({ action: "delete" });
                  }}
                >
                  {t("collection.bulk.deleteYes")}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </Sheet>
      <SalesSheet
        ids={ids}
        open={selling}
        onClose={() => {
          setSelling(false);
        }}
      />
    </>
  );
}
